import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, TextField, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomDataGrid from '../../../components/data-grid';
import CustomTooltip from '../../../components/custom-tooltip';
import MathExpressionNew from '../../../components/common/mathjax';
import Button from '../../../components/button';
import { searchIcon } from '../../../assets/images/index';
import { SolutionValidatorService } from '../../../services/solution-validator/solution-validator.service';
import './solution-analysis.scss';

interface SolutionRecord {
  id: number;
  user_id: number;
  exercise_id: number;
  question_id: number | null;
  overall_score: number;
  summary_text: string;
  detailed_feedback: any;
  created_at: string;
}

const HIDDEN_METADATA_KEYS = new Set([
  'solution_image_url',
  'solution_image_urls',
  'ocr_model_used',
  'validation_model_used',
  'llm_prompt_tokens',
  'llm_completion_tokens',
  'ocr_confidence',
  'recommendation',
]);

const SolutionAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const solutionService = new SolutionValidatorService();

  const [rows, setRows] = useState<SolutionRecord[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedSolution, setSelectedSolution] = useState<SolutionRecord | null>(null);

  // Search state
  const [searchUserIdInput, setSearchUserIdInput] = useState<string>('');
  const [activeSearchUserId, setActiveSearchUserId] = useState<number | undefined>(undefined);

  const fetchSolutions = async (currentPage: number, currentPageSize: number, targetUserId?: number) => {
    setLoading(true);
    try {
      const offset = currentPage * currentPageSize;
      const data = await solutionService.getUserExerciseSolutions(targetUserId, currentPageSize, offset);
      setRows(data || []);
      setRowCount(data && data.length > 0 ? (data.length < currentPageSize ? offset + data.length : offset + data.length + 1) : offset);
    } catch (error) {
      console.error('Failed to fetch user exercise solutions:', error);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolutions(page, pageSize, activeSearchUserId);
  }, [page, pageSize, activeSearchUserId]);

  const handleSearchClick = () => {
    const trimmed = searchUserIdInput.trim();
    if (!trimmed) {
      handleClearSearch();
      return;
    }
    const parsed = parseInt(trimmed, 10);
    if (!isNaN(parsed) && parsed > 0) {
      setActiveSearchUserId(parsed);
      setPage(0);
    }
  };

  const handleClearSearch = () => {
    setSearchUserIdInput('');
    setActiveSearchUserId(undefined);
    setPage(0);
  };

  const handleOpenModal = (record: SolutionRecord) => {
    setSelectedSolution(record);
  };

  const handleCloseModal = () => {
    setSelectedSolution(null);
  };

  const getFeedbackKeyTitle = (key: string): string => {
    const cleanKey = key.toLowerCase().trim();
    const translationKey = `feedback_${cleanKey}`;
    const translated = t(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const parseIfNeeded = (val: any): any => {
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return val;
        }
      }
    }
    return val;
  };

  const isEmpty = (val: any): boolean => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string' && val.trim() === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    if (typeof val === 'object' && Object.keys(val).length === 0) return true;
    return false;
  };

  const renderFormattedFeedback = (data: any, level: number = 0): React.ReactNode => {
    if (isEmpty(data)) {
      return level === 0 ? <Typography className="empty-feedback">{t('noDetailedFeedback')}</Typography> : null;
    }

    const parsed = parseIfNeeded(data);

    if (typeof parsed === 'string') {
      return (
        <div className="feedback-text-content">
          <MathExpressionNew content={parsed} />
        </div>
      );
    }

    if (typeof parsed === 'number' || typeof parsed === 'boolean') {
      return <span className="feedback-scalar">{String(parsed)}</span>;
    }

    if (Array.isArray(parsed)) {
      return (
        <div className="feedback-array-container">
          {parsed.map((item, idx) => {
            const parsedItem = parseIfNeeded(item);
            if (isEmpty(parsedItem)) return null;

            if (typeof parsedItem === 'string') {
              return (
                <div key={idx} className="feedback-bullet-item">
                  <span className="bullet-dot">•</span>
                  <div className="bullet-text">
                    <MathExpressionNew content={parsedItem} />
                  </div>
                </div>
              );
            }
            return (
              <div key={idx} className="feedback-card-item">
                {renderFormattedFeedback(parsedItem, level + 1)}
              </div>
            );
          })}
        </div>
      );
    }

    if (typeof parsed === 'object') {
      const entries = Object.entries(parsed).filter(
        ([key, val]) => !HIDDEN_METADATA_KEYS.has(key.toLowerCase().trim()) && !isEmpty(val)
      );

      if (entries.length === 0) {
        return null;
      }

      return (
        <div className={`feedback-object-container level-${level}`}>
          {entries.map(([key, rawVal]) => {
            const val = parseIfNeeded(rawVal);
            if (isEmpty(val)) return null;

            const title = getFeedbackKeyTitle(key);

            return (
              <div key={key} className="feedback-field-block">
                <h5 className="feedback-field-title">{title}</h5>
                <div className="feedback-field-body">
                  {renderFormattedFeedback(val, level + 1)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return <MathExpressionNew content={String(parsed)} />;
  };

  const columns = [
    {
      field: 'user_id',
      headerName: t('userId'),
      flex: 1,
      renderCell: (params: any) => <CustomTooltip content={String(params.value || '')} />,
    },
    {
      field: 'exercise_id',
      headerName: t('exerciseId'),
      flex: 1,
      renderCell: (params: any) => <CustomTooltip content={String(params.value || '')} />,
    },
    {
      field: 'question_id',
      headerName: t('questionId'),
      flex: 1,
      renderCell: (params: any) => <CustomTooltip content={params.value ? String(params.value) : '-'} />,
    },
    {
      field: 'overall_score',
      headerName: t('score'),
      flex: 1,
      renderCell: (params: any) => (
        <span className="score-badge-cell">{params.value !== undefined && params.value !== null ? params.value : 0}</span>
      ),
    },
    {
      field: 'summary',
      headerName: t('summary'),
      flex: 1.5,
      sortable: false,
      renderCell: (params: any) => (
        <button
          type="button"
          className="view-analysis-link-btn"
          onClick={() => handleOpenModal(params.row)}
        >
          {t('viewAnalysis')}
        </button>
      ),
    },
  ];

  return (
    <div className="dashboard-container solution-analysis-container">
      <div className="dashboard-table">
        <div className="search-outer-container">
          <div className="search-container">
            <TextField
              placeholder={t('searchByUserId')}
              variant="outlined"
              size="small"
              value={searchUserIdInput}
              className="dashboard-search-input"
              onChange={(e) => setSearchUserIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearchClick();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearchClick}>
                      <img src={searchIcon} alt="Search" />
                    </IconButton>
                  </InputAdornment>
                ),
                style: { direction: 'rtl' },
              }}
            />
            <Button text={t('search')} color="blue" onClick={handleSearchClick} />
            {activeSearchUserId !== undefined && (
              <span className="nav-item-text" onClick={handleClearSearch}>
                {t('clear')}
              </span>
            )}
          </div>
        </div>

        <CustomDataGrid
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          loading={loading}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        />
      </div>

      {/* Teacher Preview-style Dialog Modal */}
      <Dialog
        open={selectedSolution !== null}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        className="solution-analysis-dialog"
      >
        <DialogTitle className="dialog-header">
          <Typography variant="h6" component="span">
            {selectedSolution
              ? selectedSolution.question_id
                ? t('solutionAnalysisModalTitleWithDetails', {
                    exerciseId: selectedSolution.exercise_id,
                    questionId: selectedSolution.question_id,
                  })
                : t('solutionAnalysisModalTitleExerciseOnly', {
                    exerciseId: selectedSolution.exercise_id,
                  })
              : t('solutionAnalysisModalTitle')}
          </Typography>
          <IconButton onClick={handleCloseModal} className="close-dialog-btn">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="dialog-body" dividers>
          {selectedSolution && (
            <div className="solution-detail-body">
              {/* Section 1: סיכום ניתוח */}
              <div className="modal-section summary-section">
                <h4 className="section-title">{t('analysisSummaryHeader')}</h4>
                <div className="summary-box">
                  {selectedSolution.summary_text ? (
                    <MathExpressionNew content={selectedSolution.summary_text} />
                  ) : (
                    t('noSummaryText')
                  )}
                </div>
              </div>

              {/* Section 2: פירוט */}
              <div className="modal-section details-section">
                <h4 className="section-title">{t('analysisDetailsHeader')}</h4>
                <div className="details-box">
                  {renderFormattedFeedback(selectedSolution.detailed_feedback)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolutionAnalysis;
